import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Scale, Mail, Lock, User, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "../components/Brand";

export default function RegisterPage() {
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { register } = useAuth();
    const navigate = useNavigate();
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        
        if (password !== confirmPassword) {
            setError("Passwords don't match");
            return;
        }
        
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        
        if (username.length < 3) {
            setError("Username must be at least 3 characters");
            return;
        }
        
        setLoading(true);
        
        try {
            await register(email, password, username);
            toast.success("Welcome to The Legal Arcade!");
            navigate("/dashboard");
        } catch (err) {
            const message = err.response?.data?.detail || "Registration failed";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Navigation */}
            <nav className="px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <BrandLogo />
                </div>
            </nav>
            
            {/* Register Form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <Card className="glass border-slate-800 rounded-2xl">
                        <CardHeader className="text-center pb-2">
                            <CardTitle className="font-heading text-2xl text-white">Join the Arena</CardTitle>
                            <CardDescription className="text-slate-400">
                                Create your account and start dominating
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="username" className="text-slate-300">Username</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="username"
                                            type="text"
                                            placeholder="LegalEagle42"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            required
                                            data-testid="register-username-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@lawschool.edu"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            data-testid="register-email-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            data-testid="register-password-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            data-testid="register-confirm-password-input"
                                            className="pl-10 bg-slate-950/50 border-slate-800 focus:border-primary h-12 rounded-xl"
                                        />
                                    </div>
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="register-submit-btn"
                                    className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            Create Account
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </form>
                            
                            <div className="mt-6 text-center">
                                <p className="text-slate-400 text-sm">
                                    Already have an account?{" "}
                                    <Link 
                                        to="/login" 
                                        className="text-primary hover:underline font-medium"
                                        data-testid="register-login-link"
                                    >
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
